import { DotsThreeVertical, Export, Trash } from "@phosphor-icons/react";
import { useState } from "react";

import RestrictedComponent from "common/components/RestrictedComponent";
import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
} from "common/components/ui/Drawer";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useAudienceMutations from "../../../../datahooks/useAudienceMutations";
import { exportProfilesToCSV } from "../../../../utils";

import type { AudienceFilters, AudienceProfile } from "../../../../types";

interface AudienceTargetActionsProps {
  profile: AudienceProfile;
  filters: AudienceFilters;
}

export default function AudienceTargetActions({
  profile,
  filters,
}: AudienceTargetActionsProps) {
  const [isRemoveOpen, setIsRemoveOpen] = useState(false);
  const isDesktop = useTwBreakpoint("lg");
  const { removeProfiles, isRemovingProfiles } = useAudienceMutations(filters);

  function onRemove() {
    removeProfiles({ targetUrns: [profile.urn] }).then(() => {
      setIsRemoveOpen(false);
    });
  }

  const Component = isDesktop ? Dialog : Drawer;
  const Content = isDesktop ? DialogContent : DrawerContent;
  const Header = isDesktop ? DialogHeader : DrawerHeader;
  const Footer = isDesktop ? DialogFooter : DrawerFooter;
  const Close = isDesktop ? DialogClose : DrawerClose;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="secondary-black"
            intent="iconOnly"
            aria-label="Target actions"
            size="sm"
          >
            <DotsThreeVertical className="size-4" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" size="sm">
          <DropdownMenuList>
            <DropdownMenuItem asChild>
              <a
                href={exportProfilesToCSV([profile])}
                download="LinkedInProfile.csv"
              >
                <Export />
                Export target
              </a>
            </DropdownMenuItem>

            <RestrictedComponent visibleForPermissions={["campaigns_full"]}>
              <DropdownMenuItem
                variant="danger"
                onClick={() => setIsRemoveOpen(true)}
              >
                <Trash />
                Remove target
              </DropdownMenuItem>
            </RestrictedComponent>
          </DropdownMenuList>
        </DropdownMenuContent>
      </DropdownMenu>

      <Component open={isRemoveOpen} onOpenChange={setIsRemoveOpen}>
        <Content>
          <Header
            title="Remove target?"
            subtitle={
              <>
                Are you sure you want to remove {profile.full_name} from this
                campaign?
              </>
            }
          />

          <Footer className="justify-end">
            <Close asChild>
              <Button
                variant="secondary-black"
                onClick={() => setIsRemoveOpen(false)}
                disabled={isRemovingProfiles}
              >
                Cancel
              </Button>
            </Close>

            <Button
              onClick={onRemove}
              isLoading={isRemovingProfiles}
              variant="outline-danger"
            >
              Remove
            </Button>
          </Footer>
        </Content>
      </Component>
    </>
  );
}
